// `@bindery/speech/client` — `submitRecording()`, the multipart POST of a
// recording to the host app's transcribe route with a bearer token, its
// response parsed through `./contract`; and the submission machine a screen
// composes beside the capture machine, so a failed submit retries without
// re-recording. Compiled by tsconfig.browser.json, and by tsconfig.json via
// client.test.ts — so it imports nothing from ./capture, whose negotiation
// reads DOM-only globals the WebWorker lib does not declare.
import type * as z from "zod";
import {
  type CaptureMeta,
  FORM_FIELDS,
  type Transcript,
  type TranscriptionError,
  TranscriptionRequestFields,
  TranscriptionResponse,
  type Provenance,
  type ResponseId,
} from "./contract";

// Structurally ./capture's `Recording`, restated so this module stays out of
// the capture half's import graph (see the header).
export interface RecordingUpload {
  readonly meta: CaptureMeta;
  readonly blob: Blob;
}

// `z.input`, so `language` may be omitted and the route's default applies.
export type SubmitFields = Omit<
  z.input<typeof TranscriptionRequestFields>,
  "meta"
>;

export interface SubmitRecordingInput {
  // The host app's route, e.g. "/api/speech/transcribe".
  readonly url: string;
  readonly recording: RecordingUpload;
  readonly fields: SubmitFields;
  // The host's token source, e.g. `fetchToken` from
  // `@bindery/auth-consumer/browser`. Injected so this package does not
  // depend on one auth stack; null means signed out.
  readonly getToken: () => Promise<string | null>;
  readonly fetch?: typeof fetch;
  readonly signal?: AbortSignal;
}

const failure = (error: TranscriptionError): TranscriptionResponse => ({
  status: "error",
  error,
});

// The container named in the upload's filename, from the recorder's mime.
// The route sniffs the bytes and trusts neither. `AudioMime` has already
// admitted only webm, mp4, ogg or wav, so there is no fallback to name.
const extensionOf = (mime: string): string =>
  mime.slice("audio/".length).replace(/;.*$/, "");

const toFormData = (
  recording: RecordingUpload,
  fields: SubmitFields,
): FormData => {
  const form = new FormData();
  form.append(
    FORM_FIELDS.audio,
    recording.blob,
    `recording.${extensionOf(recording.meta.mime)}`,
  );
  form.append(FORM_FIELDS.promptText, fields.promptText);
  form.append(FORM_FIELDS.itemRef, JSON.stringify(fields.itemRef));
  if (fields.language !== undefined)
    form.append(FORM_FIELDS.language, fields.language);
  form.append(FORM_FIELDS.meta, JSON.stringify(recording.meta));
  form.append(FORM_FIELDS.attempt, String(fields.attempt));
  return form;
};

// A body that is not a `TranscriptionResponse` did not come from a route
// speaking this contract. A 5xx is a gateway or platform failure in front of
// the Worker — transient, so `provider-unavailable` with its status, which a
// child meets as try-again. Anything else — an HTML page on a 200, a
// JSON shape from another version of the route — is `provider-malformed`:
// the contract's name for "a success whose body failed the schema", here one
// hop closer to the browser. It needs an adult, which is right: it is
// version skew or misrouting, and speaking again will not fix it. `network`
// stays reserved for a fetch that rejected, as the contract defines it.
export const parseTranscriptionBody = (
  status: number,
  body: string,
): TranscriptionResponse => {
  let json: unknown;
  try {
    json = JSON.parse(body);
  } catch {
    json = undefined;
  }
  const parsed = TranscriptionResponse.safeParse(json);
  if (parsed.success) return parsed.data;
  return status >= 500
    ? failure({ kind: "provider-unavailable", status })
    : failure({ kind: "provider-malformed" });
};

// Never rejects: every failure is a `TranscriptionResponse` error value.
export const submitRecording = async (
  input: SubmitRecordingInput,
): Promise<TranscriptionResponse> => {
  // The route parses these same fields with this same schema and would
  // refuse them as `invalid-request` — but only after up to MAX_AUDIO_BYTES
  // of upload. A prompt over MAX_PROMPT_WORDS or MAX_PROMPT_CHARS is the
  // host's bug, so it is refused here with the field the route would name.
  const fields = TranscriptionRequestFields.safeParse({
    ...input.fields,
    meta: input.recording.meta,
  });
  if (!fields.success) {
    const field = fields.error.issues[0]?.path[0] ?? "fields";
    return failure({
      kind: "invalid-request",
      field: String(field).slice(0, 64),
    });
  }

  const doFetch = input.fetch ?? fetch;
  let token: string | null;
  try {
    token = await input.getToken();
  } catch {
    // The token source is itself a request; its rejection is the same
    // "could not reach our server" as the upload's would be.
    return failure({ kind: "network" });
  }
  // Signed out: no upload, and no quota spent finding out.
  if (token === null) return failure({ kind: "unauthenticated" });

  let res: Response;
  let body: string;
  try {
    res = await doFetch(input.url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: toFormData(input.recording, input.fields),
      cache: "no-store",
      ...(input.signal === undefined ? {} : { signal: input.signal }),
    });
    body = await res.text();
  } catch {
    return failure({ kind: "network" });
  }
  return parseTranscriptionBody(res.status, body);
};

// ---------------------------------------------------------------------------
// Submission machine
// ---------------------------------------------------------------------------

export type SubmissionState =
  | { readonly tag: "idle" }
  | { readonly tag: "submitting" }
  | {
      readonly tag: "transcribed";
      readonly responseId: ResponseId;
      readonly transcript: Transcript;
      readonly provenance: Provenance;
    }
  // Retry is `submit-requested` again with the same recording; whether that
  // is worth offering is `childFacing(error)`'s call.
  | { readonly tag: "failed"; readonly error: TranscriptionError };

export type SubmissionEvent =
  | { readonly type: "submit-requested" }
  | {
      readonly type: "response-received";
      readonly response: TranscriptionResponse;
    }
  // A new recording (or a redo) makes any previous result stale.
  | { readonly type: "reset" };

// Unhandled pairs return `s` itself, so a second tap while submitting — or
// after success — is a no-op the screen can use to skip the POST.
export const reduceSubmission = (
  s: SubmissionState,
  e: SubmissionEvent,
): SubmissionState => {
  switch (e.type) {
    case "submit-requested":
      return s.tag === "idle" || s.tag === "failed" ? { tag: "submitting" } : s;
    case "response-received":
      if (s.tag !== "submitting") return s;
      return e.response.status === "ok"
        ? {
            tag: "transcribed",
            responseId: e.response.responseId,
            transcript: e.response.transcript,
            provenance: e.response.provenance,
          }
        : { tag: "failed", error: e.response.error };
    case "reset":
      return s.tag === "idle" ? s : { tag: "idle" };
  }
};
